import React, { useState, useEffect, useRef, useCallback } from 'react';
import styled from 'styled-components';
import { toast } from 'react-toastify';
import { useNavigate, useParams } from 'react-router-dom';
import { Layout } from '../components/Layout';
import { useTheme } from '../context/ThemeContext';
import { workOrderService, customerService, userService } from '../api';
import certificateService from '../api/services/certificateService';
import { Button, Input, ThemedText, Avatar } from '../components';
import { CustomerFormModal } from '../components/CustomerFormModal';
import { LocationFormModal } from '../components/LocationFormModal';
import { extractItems } from '../utils/responseUtils';
import { useRole } from '../hooks/useRole';
import { useTimezone } from '../context/TimezoneContext';
import { getLocalDateString } from '../utils/dateUtils';
import { StatusPalette, withOpacity } from '../constants/statusColors';
import { Check, AlertTriangle, X, ArrowLeft } from 'lucide-react';
import { PageHeaderRow } from '../components/PageStyles';
import { FormLabel as Label, StyledSelect as Select } from '../components/FormStyles';

const PageContainer = styled.div`
  max-width: 800px;
  margin: 0 auto;
`;

const FormCard = styled.div`
  background-color: ${props => props.theme.colors.backgroundSecondary};
  border: 1px solid ${props => props.theme.colors.border};
  border-radius: ${props => props.theme.borderRadius.lg};
  padding: ${props => props.theme.spacing.xl};
  box-shadow: ${props => props.theme.shadows.sm || '0 1px 3px rgba(0,0,0,0.08)'};
`;

const SectionHeader = styled.div`
  font-size: ${props => props.theme.typography.sizes.default};
  font-weight: 700;
  color: ${props => props.theme.colors.text};
  margin-bottom: ${props => props.theme.spacing.md};
  padding-bottom: ${props => props.theme.spacing.sm};
  border-bottom: 2px solid ${props => props.theme.colors.border};
  text-transform: uppercase;
  letter-spacing: 0.5px;
`;

const SectionDivider = styled.div`
  margin-top: ${props => props.theme.spacing.lg};
  margin-bottom: ${props => props.theme.spacing.md};
`;

const FormGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${props => props.theme.spacing.xs};
  margin-bottom: ${props => props.theme.spacing.md};
`;

const TextArea = styled.textarea`
  padding: ${props => props.theme.spacing.sm} ${props => props.theme.spacing.md};
  border: 1px solid ${props => props.theme.colors.border};
  border-radius: ${props => props.theme.borderRadius.md};
  background-color: ${props => props.theme.colors.background};
  color: ${props => props.theme.colors.text};
  font-size: ${props => props.theme.typography.sizes.default};
  font-family: inherit;
  min-height: 80px;
  resize: none;
  overflow: hidden;

  &:focus {
    outline: none;
    border-color: ${props => props.theme.colors.tint};
  }
`;

const CharCount = styled.div`
  text-align: right;
  font-size: ${props => props.theme.typography.sizes.small};
  color: ${props => props.count > props.max ? StatusPalette.red : props.theme.colors.muted};
`;

const FormRow = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: ${props => props.theme.spacing.md};
`;

const FieldWithAction = styled.div`
  display: flex;
  gap: ${props => props.theme.spacing.sm};
  align-items: flex-start;

  & > *:first-child {
    flex: 1;
  }
`;

const InlineButton = styled.button`
  padding: ${props => props.theme.spacing.sm} ${props => props.theme.spacing.md};
  border: 1px solid ${props => props.theme.colors.tint};
  border-radius: ${props => props.theme.borderRadius.md};
  background-color: transparent;
  color: ${props => props.theme.colors.tint};
  font-size: ${props => props.theme.typography.sizes.small};
  cursor: pointer;
  white-space: nowrap;
  margin-top: 0;
  height: 40px;

  &:hover {
    background-color: ${props => props.theme.colors.tint}10;
  }
`;

const PriorityGroup = styled.div`
  display: flex;
  gap: ${props => props.theme.spacing.sm};
  flex-wrap: wrap;
`;

const WO_PRIORITY_COLORS = {
  Low: StatusPalette.green,
  Medium: StatusPalette.amber,
  High: StatusPalette.orange,
  Urgent: StatusPalette.red,
};

const PriorityButton = styled.button`
  padding: ${props => props.theme.spacing.sm} ${props => props.theme.spacing.lg};
  border: 2px solid ${props => WO_PRIORITY_COLORS[props.priority] || props.theme.colors.border};
  border-radius: ${props => props.theme.borderRadius.md};
  background-color: ${props => props.selected ? WO_PRIORITY_COLORS[props.priority] || 'transparent' : 'transparent'};
  color: ${props => props.selected ? (props.priority === 'Medium' ? '#000' : '#fff') : WO_PRIORITY_COLORS[props.priority] || props.theme.colors.text};
  font-size: ${props => props.theme.typography.sizes.default};
  font-weight: 600;
  cursor: pointer;
  transition: all 0.15s ease;

  &:hover {
    opacity: 0.85;
    background-color: ${props => {
      const c = WO_PRIORITY_COLORS[props.priority];
      return c ? withOpacity(c) : 'transparent';
    }};
    ${props => props.selected && `
      background-color: ${WO_PRIORITY_COLORS[props.priority]};
      opacity: 0.85;
    `}
  }
`;

const DateFieldWrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${props => props.theme.spacing.xs};
`;

const QuickDateChips = styled.div`
  display: flex;
  gap: ${props => props.theme.spacing.xs};
  flex-wrap: wrap;
`;

const DateChip = styled.button`
  padding: 4px ${props => props.theme.spacing.sm};
  border: 1px solid ${props => props.selected ? props.theme.colors.tint : props.theme.colors.border};
  border-radius: 20px;
  background-color: ${props => props.selected ? props.theme.colors.tint : 'transparent'};
  color: ${props => props.selected ? '#fff' : props.theme.colors.muted};
  font-size: ${props => props.theme.typography.sizes.small};
  cursor: pointer;
  transition: all 0.15s ease;

  &:hover {
    border-color: ${props => props.theme.colors.tint};
    color: ${props => props.selected ? '#fff' : props.theme.colors.tint};
  }
`;

const FooterActions = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: ${props => props.theme.spacing.md};
  margin-top: ${props => props.theme.spacing.xl};
`;

const BackButton = styled.button`
  display: flex;
  align-items: center;
  gap: ${props => props.theme.spacing.xs};
  background: none;
  border: none;
  color: ${props => props.theme.colors.tint};
  cursor: pointer;
  font-size: ${props => props.theme.typography.sizes.default};
  padding: 0;

  &:hover {
    text-decoration: underline;
  }
`;

const UserChipsContainer = styled.div`
  display: flex;
  gap: ${props => props.theme.spacing.sm};
  flex-wrap: wrap;
`;

const UserChip = styled.button`
  display: flex;
  align-items: center;
  gap: ${props => props.theme.spacing.sm};
  padding: 6px ${props => props.theme.spacing.md};
  border: 2px solid ${props => {
    if (props.selected) return StatusPalette.green;
    if (props.$certStatus === 'valid') return props.theme.colors.tint;
    if (props.$certStatus === 'expiring') return StatusPalette.amber;
    return props.theme.colors.border;
  }};
  border-radius: 24px;
  background-color: ${props => {
    if (props.selected) return `${StatusPalette.green}15`;
    if (props.$certStatus === 'valid') return `${props.theme.colors.tint}15`;
    if (props.$certStatus === 'expiring') return `${StatusPalette.amber}12`;
    return 'transparent';
  }};
  color: ${props =>
    (props.$certStatus === 'missing' || props.$certStatus === 'expired')
      ? props.theme.colors.muted
      : props.theme.colors.text
  };
  font-size: ${props => props.theme.typography.sizes.small};
  cursor: pointer;
  transition: all 0.15s ease;

  &:hover {
    border-color: ${props => props.theme.colors.tint};
    background-color: ${props => props.theme.colors.tint}10;
    color: ${props => props.theme.colors.text};
  }
`;

const UserAvatar = styled.div`
  width: 28px;
  height: 28px;
  border-radius: 50%;
  background-color: ${props => props.selected ? props.theme.colors.tint : props.theme.colors.muted};
  color: #fff;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 11px;
  font-weight: 700;
  text-transform: uppercase;
  flex-shrink: 0;
`;

const SearchableDropdownWrapper = styled.div`
  position: relative;
  flex: 1;
`;

const SearchInput = styled.input`
  width: 100%;
  padding: ${props => props.theme.spacing.sm} ${props => props.theme.spacing.md};
  border: 1px solid ${props => props.focused ? props.theme.colors.tint : props.theme.colors.border};
  border-radius: ${props => props.theme.borderRadius.md};
  background-color: ${props => props.theme.colors.background};
  color: ${props => props.theme.colors.text};
  font-size: ${props => props.theme.typography.sizes.default};
  box-sizing: border-box;

  &:focus {
    outline: none;
    border-color: ${props => props.theme.colors.tint};
  }
`;

const DropdownList = styled.div`
  position: absolute;
  top: 100%;
  left: 0;
  right: 0;
  max-height: 200px;
  overflow-y: auto;
  background-color: ${props => props.theme.colors.background};
  border: 1px solid ${props => props.theme.colors.border};
  border-radius: ${props => props.theme.borderRadius.md};
  box-shadow: ${props => props.theme.shadows.lg};
  z-index: 100;
  margin-top: 4px;
`;

const DropdownItem = styled.div`
  padding: ${props => props.theme.spacing.sm} ${props => props.theme.spacing.md};
  cursor: pointer;
  color: ${props => props.theme.colors.text};
  font-size: ${props => props.theme.typography.sizes.default};
  background-color: ${props => props.selected ? props.theme.colors.tint + '15' : 'transparent'};

  &:hover {
    background-color: ${props => props.theme.colors.backgroundSecondary};
  }
`;

const HintText = styled.div`
  font-size: ${props => props.theme.typography.sizes.small};
  color: ${props => props.theme.colors.muted};
  margin-top: 2px;
`;

const CertBadge = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 18px;
  height: 18px;
  border-radius: 50%;
  font-size: 10px;
  font-weight: 700;
  flex-shrink: 0;
  background-color: ${props =>
    props.$status === 'valid' ? StatusPalette.green :
    props.$status === 'expiring' ? StatusPalette.amber :
    props.$status === 'expired' ? StatusPalette.red :
    'transparent'
  };
  color: ${props => props.$status === 'expiring' ? '#000' : '#fff'};
  ${props => props.$status === 'missing' && `
    border: 2px solid ${props.theme.colors.border};
    background: transparent;
  `}
`;

const PRIORITY_OPTIONS = ['Low', 'Medium', 'High', 'Urgent'];
const DESCRIPTION_MAX = 1000;

const getQuickDate = (offset, timezoneId) => {
  const offsetDays = offset === 'tomorrow' ? 1 : offset === 'next_week' ? 7 : 0;
  if (offset === 'next_month') {
    const tz = timezoneId || Intl.DateTimeFormat().resolvedOptions().timeZone;
    const d = new Date();
    d.setMonth(d.getMonth() + 1);
    return new Intl.DateTimeFormat('en-CA', { timeZone: tz, year: 'numeric', month: '2-digit', day: '2-digit' }).format(d);
  }
  return getLocalDateString(timezoneId, offsetDays);
};

const QUICK_DATES = [
  { label: 'Today', key: 'today' },
  { label: 'Tomorrow', key: 'tomorrow' },
  { label: 'Next Week', key: 'next_week' },
  { label: 'Next Month', key: 'next_month' },
];

const resolveData = extractItems;

const getInitials = (firstName, lastName) => {
  return ((firstName?.[0] || '') + (lastName?.[0] || '')).toUpperCase() || '?';
};

// Searchable dropdown component
const SearchableDropdown = ({ items, value, onChange, placeholder, displayFn, filterFn, disabled, theme }) => {
  const [search, setSearch] = useState('');
  const [isOpen, setIsOpen] = useState(false);
  const wrapperRef = useRef(null);

  const selectedItem = items.find(item => String(item.id) === String(value));
  const displayValue = selectedItem ? displayFn(selectedItem) : '';

  const filtered = search
    ? items.filter(item => filterFn(item, search.toLowerCase()))
    : items;

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target)) {
        setIsOpen(false);
        setSearch('');
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleSelect = (item) => {
    onChange(String(item.id));
    setIsOpen(false);
    setSearch('');
  };

  const handleClear = () => {
    onChange('');
    setSearch('');
  };

  return (
    <SearchableDropdownWrapper ref={wrapperRef}>
      <SearchInput
        theme={theme}
        value={isOpen ? search : displayValue}
        onChange={(e) => { setSearch(e.target.value); if (!isOpen) setIsOpen(true); }}
        onFocus={() => { setIsOpen(true); setSearch(''); }}
        placeholder={disabled ? placeholder : (displayValue || placeholder)}
        disabled={disabled}
        focused={isOpen}
      />
      {isOpen && !disabled && (
        <DropdownList theme={theme}>
          {value && (
            <DropdownItem theme={theme} onClick={handleClear} style={{ fontStyle: 'italic', color: theme.colors.muted }}>
              Clear selection
            </DropdownItem>
          )}
          {filtered.length === 0 ? (
            <DropdownItem theme={theme} style={{ color: theme.colors.muted, cursor: 'default' }}>
              No results found
            </DropdownItem>
          ) : (
            filtered.map(item => (
              <DropdownItem
                key={item.id}
                theme={theme}
                selected={String(item.id) === String(value)}
                onClick={() => handleSelect(item)}
              >
                {displayFn(item)}
              </DropdownItem>
            ))
          )}
        </DropdownList>
      )}
    </SearchableDropdownWrapper>
  );
};

const CreateWorkOrderPage = () => {
  const theme = useTheme();
  const navigate = useNavigate();
  const { id } = useParams();
  const { isAdmin } = useRole();
  const { effectiveTimezone } = useTimezone();
  const isEditMode = Boolean(id);
  const descriptionRef = useRef(null);
  const originalAssignedIdsRef = useRef([]);
  const [customers, setCustomers] = useState([]);
  const [users, setUsers] = useState([]);
  const [locations, setLocations] = useState([]);
  const [certDefinitions, setCertDefinitions] = useState([]);
  const [selectedCertIds, setSelectedCertIds] = useState([]);
  const [userCertSummary, setUserCertSummary] = useState([]);
  const [isCustomerModalOpen, setIsCustomerModalOpen] = useState(false);
  const [isLocationModalOpen, setIsLocationModalOpen] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [loadingWorkOrder, setLoadingWorkOrder] = useState(false);

  const [formData, setFormData] = useState({
    title: '',
    description: '',
    customerId: '',
    locationId: '',
    assignedToUserIds: [],
    priority: 'Medium',
    serviceDate: '',
    dueDate: '',
    externalReference: '',
  });

  useEffect(() => {
    if (!isAdmin()) {
      toast.error('You do not have permission to create or edit work orders');
      navigate('/work-orders');
    }
  }, [isAdmin, navigate]);

  useEffect(() => {
    fetchDropdownData();
  }, []);

  useEffect(() => {
    if (isEditMode) {
      fetchWorkOrder();
    }
  }, [id]);

  const fetchWorkOrder = async () => {
    try {
      setLoadingWorkOrder(true);
      const response = await workOrderService.getWorkOrderById(id);
      const wo = response?.data || response;
      const custId = wo.customerId ? String(wo.customerId) : '';
      const loadedAssignedIds = wo.assignments?.length
        ? wo.assignments.map(a => a.userId)
        : (wo.assignedToUserId ? [wo.assignedToUserId] : []);
      originalAssignedIdsRef.current = [...loadedAssignedIds].sort((a, b) => a - b);
      setFormData({
        title: wo.title || '',
        description: wo.description || '',
        customerId: custId,
        locationId: wo.locationId ? String(wo.locationId) : '',
        assignedToUserIds: loadedAssignedIds,
        priority: wo.priority || 'Medium',
        serviceDate: wo.serviceDate ? wo.serviceDate.substring(0, 10) : '',
        dueDate: wo.dueDate ? wo.dueDate.substring(0, 10) : '',
        externalReference: wo.externalReference || '',
      });
      if (wo.requiredCertificates?.length) {
        setSelectedCertIds(wo.requiredCertificates.map(c => c.certificateDefinitionId));
      }
      if (custId) {
        fetchLocationsForCustomer(custId);
      }
    } catch (error) {
      console.error('Failed to fetch work order:', error);
      toast.error('Failed to load work order');
      navigate('/work-orders');
    } finally {
      setLoadingWorkOrder(false);
    }
  };

  // Auto-resize description textarea
  const autoResizeTextarea = useCallback(() => {
    const el = descriptionRef.current;
    if (el) {
      el.style.height = 'auto';
      el.style.height = Math.max(80, el.scrollHeight) + 'px';
    }
  }, []);

  useEffect(() => {
    autoResizeTextarea();
  }, [formData.description, autoResizeTextarea]);

  const fetchDropdownData = async () => {
    try {
      const [customersResponse, usersResponse, certResponse, certSummaryResponse] = await Promise.all([
        customerService.getCustomers(),
        userService.getUsers({}),
        certificateService.getDefinitions(),
        certificateService.getOrgUsersCertificateSummary(),
      ]);
      setCustomers(resolveData(customersResponse));
      setUsers(resolveData(usersResponse));
      const certs = Array.isArray(certResponse) ? certResponse : (certResponse?.data || []);
      setCertDefinitions(certs);
      const summary = Array.isArray(certSummaryResponse) ? certSummaryResponse : (certSummaryResponse?.data || []);
      setUserCertSummary(summary);
    } catch (error) {
      console.error('Failed to fetch dropdown data:', error);
    }
  };

  const fetchLocationsForCustomer = async (customerId) => {
    if (!customerId) {
      setLocations([]);
      return;
    }
    try {
      const response = await customerService.getCustomerLocations(customerId);
      setLocations(resolveData(response));
    } catch (error) {
      console.error('Failed to fetch locations:', error);
      setLocations([]);
    }
  };

  const handleCustomerChange = (customerId) => {
    setFormData(prev => ({ ...prev, customerId, locationId: '' }));
    fetchLocationsForCustomer(customerId);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    // In edit mode, only send assignedToUserIds if the user actually changed them
    // Backend DTO semantics: null = don't change, [] = unassign all, [ids] = replace
    let assignedToUserIds;
    if (isEditMode) {
      const currentIds = [...formData.assignedToUserIds].sort((a, b) => a - b);
      const originalIds = originalAssignedIdsRef.current;
      const assignmentsChanged = JSON.stringify(currentIds) !== JSON.stringify(originalIds);
      assignedToUserIds = assignmentsChanged
        ? (formData.assignedToUserIds.length > 0 ? formData.assignedToUserIds : [])
        : null;
    } else {
      assignedToUserIds = formData.assignedToUserIds.length > 0 ? formData.assignedToUserIds : null;
    }

    const payload = {
      ...formData,
      customerId: formData.customerId ? parseInt(formData.customerId) : null,
      locationId: formData.locationId ? parseInt(formData.locationId) : null,
      assignedToUserIds,
      requiredCertificateIds: selectedCertIds.length > 0 ? selectedCertIds : null,
    };

    try {
      setSubmitting(true);
      if (isEditMode) {
        await workOrderService.updateWorkOrder(id, payload);
        toast.success('Work order updated successfully');
      } else {
        await workOrderService.createWorkOrder(payload);
        toast.success('Work order created successfully');
      }
      navigate('/work-orders');
    } catch (error) {
      console.error(`Failed to ${isEditMode ? 'update' : 'create'} work order:`, error);
      toast.error(error.response?.data?.message || `Failed to ${isEditMode ? 'update' : 'create'} work order`);
    } finally {
      setSubmitting(false);
    }
  };

  const handleCustomerCreated = async (customer) => {
    try {
      const response = await customerService.getCustomers();
      const updatedCustomers = resolveData(response);
      setCustomers(updatedCustomers);

      if (customer && customer.id) {
        setFormData(prev => ({ ...prev, customerId: String(customer.id), locationId: '' }));
        fetchLocationsForCustomer(customer.id);
      }
    } catch (error) {
      console.error('Failed to refresh customers:', error);
    }
  };

  const handleLocationCreated = async (location) => {
    if (formData.customerId) {
      try {
        const response = await customerService.getCustomerLocations(formData.customerId);
        const updatedLocations = resolveData(response);
        setLocations(updatedLocations);

        if (location && location.id) {
          setFormData(prev => ({ ...prev, locationId: String(location.id) }));
        }
      } catch (error) {
        console.error('Failed to refresh locations:', error);
      }
    }
  };

  const customerDisplayFn = (c) => `${c.firstName} ${c.lastName}${c.company ? ` (${c.company})` : ''}`;
  const customerFilterFn = (c, term) =>
    c.firstName?.toLowerCase().includes(term) ||
    c.lastName?.toLowerCase().includes(term) ||
    c.company?.toLowerCase().includes(term) ||
    c.email?.toLowerCase().includes(term);

  const getUserCertStatus = (userId) => {
    if (selectedCertIds.length === 0) return null;
    const summary = userCertSummary.find(s => s.userId === userId);
    if (!summary) return 'missing';
    const hasAll = selectedCertIds.every(id => summary.certificateDefinitionIds.includes(id));
    if (!hasAll) return 'missing';
    const anyExpired = selectedCertIds.some(id => summary.expiredCertificateDefinitionIds.includes(id));
    if (anyExpired) return 'expired';
    const anyExpiringSoon = selectedCertIds.some(id => summary.expiringSoonCertificateDefinitionIds.includes(id));
    if (anyExpiringSoon) return 'expiring';
    return 'valid';
  };

  const getSortedUsers = () => {
    if (selectedCertIds.length === 0) return users;
    const statusOrder = { valid: 0, expiring: 1, expired: 2, missing: 3 };
    return [...users].sort((a, b) => {
      const sa = statusOrder[getUserCertStatus(a.id)] ?? 3;
      const sb = statusOrder[getUserCertStatus(b.id)] ?? 3;
      return sa - sb;
    });
  };

  const locationDisplayFn = (loc) => loc.name || loc.address || '';
  const locationFilterFn = (loc, term) =>
    loc.name?.toLowerCase().includes(term) ||
    loc.address?.toLowerCase().includes(term) ||
    loc.city?.toLowerCase().includes(term);

  return (
    <Layout currentPath="/work-orders">
      <PageContainer theme={theme}>
        <PageHeaderRow theme={theme}>
          <div>
            <BackButton theme={theme} onClick={() => navigate('/work-orders')}>
              <ArrowLeft size={14} /> Back to Work Orders
            </BackButton>
            <ThemedText type="title" style={{ marginTop: 8 }}>{isEditMode ? 'Edit Work Order' : 'New Work Order'}</ThemedText>
          </div>
        </PageHeaderRow>

        <FormCard theme={theme}>
          <form onSubmit={handleSubmit}>
            {/* Section: Details */}
            <SectionHeader theme={theme}>Details</SectionHeader>

            <FormGroup theme={theme}>
              <Label theme={theme}>Title *</Label>
              <Input
                required
                value={formData.title}
                onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                placeholder="Enter work order title"
              />
            </FormGroup>

            <FormGroup theme={theme}>
              <Label theme={theme}>Description</Label>
              <TextArea
                ref={descriptionRef}
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                placeholder="Enter description"
                theme={theme}
              />
              <CharCount theme={theme} count={formData.description.length} max={DESCRIPTION_MAX}>
                {formData.description.length}/{DESCRIPTION_MAX}
              </CharCount>
            </FormGroup>

            <FormGroup theme={theme}>
              <Label theme={theme}>Priority</Label>
              <PriorityGroup theme={theme}>
                {PRIORITY_OPTIONS.map(p => (
                  <PriorityButton
                    key={p}
                    type="button"
                    theme={theme}
                    priority={p}
                    selected={formData.priority === p}
                    onClick={() => setFormData({ ...formData, priority: p })}
                  >
                    {p}
                  </PriorityButton>
                ))}
              </PriorityGroup>
            </FormGroup>

            {/* Section: Customer & Location */}
            <SectionDivider theme={theme}>
              <SectionHeader theme={theme}>Customer & Location</SectionHeader>
            </SectionDivider>

            <FormGroup theme={theme}>
              <Label theme={theme}>Customer</Label>
              <FieldWithAction theme={theme}>
                <SearchableDropdown
                  items={customers}
                  value={formData.customerId}
                  onChange={handleCustomerChange}
                  placeholder="Search customers..."
                  displayFn={customerDisplayFn}
                  filterFn={customerFilterFn}
                  theme={theme}
                />
                <InlineButton
                  type="button"
                  theme={theme}
                  onClick={() => setIsCustomerModalOpen(true)}
                >
                  + New Customer
                </InlineButton>
              </FieldWithAction>
            </FormGroup>

            <FormGroup theme={theme}>
              <Label theme={theme}>Location</Label>
              <FieldWithAction theme={theme}>
                <SearchableDropdown
                  items={locations}
                  value={formData.locationId}
                  onChange={(val) => setFormData({ ...formData, locationId: val })}
                  placeholder={formData.customerId ? 'Search locations...' : 'Select a customer first'}
                  displayFn={locationDisplayFn}
                  filterFn={locationFilterFn}
                  disabled={!formData.customerId}
                  theme={theme}
                />
                <InlineButton
                  type="button"
                  theme={theme}
                  onClick={() => setIsLocationModalOpen(true)}
                  disabled={!formData.customerId}
                  style={{ opacity: formData.customerId ? 1 : 0.5 }}
                >
                  + New Location
                </InlineButton>
              </FieldWithAction>
            </FormGroup>

            {/* Section: Required Certificates */}
            {certDefinitions.length > 0 && (
              <>
                <SectionDivider theme={theme}>
                  <SectionHeader theme={theme}>Required Certificates</SectionHeader>
                </SectionDivider>

                <FormGroup theme={theme}>
                  <HintText theme={theme} style={{ marginBottom: '12px' }}>
                    Select which certificates the assigned worker must hold for this work order.
                  </HintText>
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
                    {certDefinitions.map(def => (
                      <label
                        key={def.id}
                        style={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: '6px',
                          padding: '8px 14px',
                          borderRadius: '8px',
                          border: `1px solid ${selectedCertIds.includes(def.id) ? theme.colors.tint : theme.colors.border}`,
                          backgroundColor: selectedCertIds.includes(def.id) ? `${theme.colors.tint}15` : theme.colors.backgroundSecondary,
                          cursor: 'pointer',
                          fontSize: '14px',
                          color: theme.colors.text,
                          transition: 'all 0.15s ease',
                          userSelect: 'none',
                        }}
                      >
                        <input
                          type="checkbox"
                          checked={selectedCertIds.includes(def.id)}
                          onChange={(e) => {
                            if (e.target.checked) {
                              setSelectedCertIds(prev => [...prev, def.id]);
                            } else {
                              setSelectedCertIds(prev => prev.filter(id => id !== def.id));
                            }
                          }}
                          style={{ accentColor: theme.colors.tint }}
                        />
                        {def.name}
                      </label>
                    ))}
                  </div>
                </FormGroup>
              </>
            )}

            {/* Section: Assignment & Scheduling */}
            <SectionDivider theme={theme}>
              <SectionHeader theme={theme}>Assignment & Scheduling</SectionHeader>
            </SectionDivider>

            <FormGroup theme={theme}>
              <Label theme={theme}>Assigned To</Label>
              {users.length > 0 ? (
                <>
                  <UserChipsContainer theme={theme}>
                    {getSortedUsers().map(u => {
                      const certStatus = getUserCertStatus(u.id);
                      return (
                        <UserChip
                          key={u.id}
                          type="button"
                          theme={theme}
                          selected={formData.assignedToUserIds.includes(u.id)}
                          $certStatus={certStatus}
                          onClick={() => setFormData(prev => ({
                            ...prev,
                            assignedToUserIds: prev.assignedToUserIds.includes(u.id)
                              ? prev.assignedToUserIds.filter(uid => uid !== u.id)
                              : [...prev.assignedToUserIds, u.id]
                          }))}
                        >
                          <Avatar src={u.profilePicture} name={`${u.firstName} ${u.lastName}`} size="sm" />
                          {u.firstName} {u.lastName}
                          {formData.assignedToUserIds.includes(u.id) && <CertBadge $status="valid" theme={theme}><Check size={12} /></CertBadge>}
                          {!formData.assignedToUserIds.includes(u.id) && certStatus === 'expiring' && <CertBadge $status="expiring" theme={theme}><AlertTriangle size={12} /></CertBadge>}
                          {!formData.assignedToUserIds.includes(u.id) && certStatus === 'expired' && <CertBadge $status="expired" theme={theme}><X size={12} /></CertBadge>}
                        </UserChip>
                      );
                    })}
                  </UserChipsContainer>
                  {selectedCertIds.length > 0 && (
                    <HintText theme={theme} style={{ marginTop: '8px' }}>
                      Blue border = holds required certificates &nbsp; <AlertTriangle size={12} /> = some expiring soon &nbsp; <X size={12} /> = expired &nbsp; <Check size={12} /> = selected
                    </HintText>
                  )}
                </>
              ) : (
                <HintText theme={theme}>No users available</HintText>
              )}
            </FormGroup>

            <FormRow theme={theme}>
              <FormGroup theme={theme}>
                <Label theme={theme}>Service Date</Label>
                <DateFieldWrapper theme={theme}>
                  <Input
                    type="date"
                    value={formData.serviceDate}
                    onChange={(e) => setFormData({ ...formData, serviceDate: e.target.value })}
                  />
                  <QuickDateChips theme={theme}>
                    {QUICK_DATES.map(qd => (
                      <DateChip
                        key={qd.key}
                        type="button"
                        theme={theme}
                        selected={formData.serviceDate === getQuickDate(qd.key, effectiveTimezone)}
                        onClick={() => setFormData({ ...formData, serviceDate: getQuickDate(qd.key, effectiveTimezone) })}
                      >
                        {qd.label}
                      </DateChip>
                    ))}
                    {formData.serviceDate && (
                      <DateChip
                        type="button"
                        theme={theme}
                        onClick={() => setFormData({ ...formData, serviceDate: '' })}
                      >
                        Clear
                      </DateChip>
                    )}
                  </QuickDateChips>
                </DateFieldWrapper>
              </FormGroup>

              <FormGroup theme={theme}>
                <Label theme={theme}>Due Date</Label>
                <DateFieldWrapper theme={theme}>
                  <Input
                    type="date"
                    value={formData.dueDate}
                    onChange={(e) => setFormData({ ...formData, dueDate: e.target.value })}
                  />
                  <QuickDateChips theme={theme}>
                    {QUICK_DATES.map(qd => (
                      <DateChip
                        key={qd.key}
                        type="button"
                        theme={theme}
                        selected={formData.dueDate === getQuickDate(qd.key, effectiveTimezone)}
                        onClick={() => setFormData({ ...formData, dueDate: getQuickDate(qd.key, effectiveTimezone) })}
                      >
                        {qd.label}
                      </DateChip>
                    ))}
                    {formData.dueDate && (
                      <DateChip
                        type="button"
                        theme={theme}
                        onClick={() => setFormData({ ...formData, dueDate: '' })}
                      >
                        Clear
                      </DateChip>
                    )}
                  </QuickDateChips>
                </DateFieldWrapper>
              </FormGroup>
            </FormRow>

            {/* Section: References */}
            <SectionDivider theme={theme}>
              <SectionHeader theme={theme}>References</SectionHeader>
            </SectionDivider>

            <FormGroup theme={theme}>
              <Label theme={theme}>External Reference</Label>
              <Input
                value={formData.externalReference}
                onChange={(e) => setFormData({ ...formData, externalReference: e.target.value })}
                placeholder="e.g. PO-12345, INV-2024-001"
              />
              <HintText theme={theme}>
                Optional. Use this to link to an external system such as a PO number, invoice, or ticket ID.
              </HintText>
            </FormGroup>

            <FooterActions theme={theme}>
              <Button
                type="button"
                variant="secondary"
                onClick={() => navigate('/work-orders')}
              >
                Cancel
              </Button>
              <Button type="submit" disabled={submitting || loadingWorkOrder}>
                {submitting ? (isEditMode ? 'Updating...' : 'Creating...') : (isEditMode ? 'Update Work Order' : 'Create Work Order')}
              </Button>
            </FooterActions>
          </form>
        </FormCard>

        <CustomerFormModal
          isOpen={isCustomerModalOpen}
          onClose={() => setIsCustomerModalOpen(false)}
          onSuccess={handleCustomerCreated}
        />

        <LocationFormModal
          isOpen={isLocationModalOpen}
          onClose={() => setIsLocationModalOpen(false)}
          onSuccess={handleLocationCreated}
          preselectedCustomerId={formData.customerId}
        />
      </PageContainer>
    </Layout>
  );
};

export default CreateWorkOrderPage;
export { CreateWorkOrderPage };
